import * as THREE from 'three';

interface RingEffect {
  mesh: THREE.Mesh;
  material: THREE.MeshBasicMaterial;
  life: number;
  duration: number;
  fromScale: number;
  toScale: number;
}

interface Spark {
  mesh: THREE.Mesh;
  material: THREE.MeshBasicMaterial;
  velocity: THREE.Vector3;
  life: number;
  duration: number;
}

const RING_POOL = 6;
const SPARK_POOL = 16;

/**
 * Pooled event-driven effects: expanding shock rings (pickup, crash) and
 * short-lived spark shards (near miss, crash debris). Additive, no shadows,
 * only active effects draw.
 */
export class VfxSystem {
  readonly group = new THREE.Group();

  private readonly rings: RingEffect[] = [];
  private readonly sparks: Spark[] = [];
  private readonly disposables: Array<{ dispose(): void }> = [];
  // Cosmetic-only jitter; deterministic so captures stay reproducible.
  private jitterState = 1234567;

  private jitter(): number {
    this.jitterState = (this.jitterState * 1664525 + 1013904223) >>> 0;
    return this.jitterState / 0xffffffff;
  }

  constructor() {
    const ringGeometry = new THREE.TorusGeometry(0.5, 0.045, 6, 32);
    const sparkGeometry = new THREE.PlaneGeometry(0.09, 0.46);
    this.disposables.push(ringGeometry, sparkGeometry);

    for (let i = 0; i < RING_POOL; i += 1) {
      const material = new THREE.MeshBasicMaterial({
        color: '#ffffff',
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      const mesh = new THREE.Mesh(ringGeometry, material);
      mesh.rotation.x = -Math.PI / 2;
      mesh.visible = false;
      this.group.add(mesh);
      this.rings.push({ mesh, material, life: 0, duration: 0.4, fromScale: 0.4, toScale: 2 });
      this.disposables.push(material);
    }

    for (let i = 0; i < SPARK_POOL; i += 1) {
      const material = new THREE.MeshBasicMaterial({
        color: '#ffffff',
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      const mesh = new THREE.Mesh(sparkGeometry, material);
      mesh.visible = false;
      this.group.add(mesh);
      this.sparks.push({
        mesh,
        material,
        velocity: new THREE.Vector3(),
        life: 0,
        duration: 0.5,
      });
      this.disposables.push(material);
    }
  }

  /** Expanding horizontal shock ring (pickup, overdrive, crash). */
  burst(x: number, y: number, z: number, color: string, scale = 1, duration = 0.38): void {
    const ring = this.rings.find((r) => r.life <= 0) ?? this.rings[0];
    ring.material.color.set(color);
    ring.mesh.position.set(x, y, z);
    ring.life = duration;
    ring.duration = duration;
    ring.fromScale = 0.35 * scale;
    ring.toScale = 2.4 * scale;
    ring.mesh.visible = true;
  }

  /** Radial spark shards (near miss graze, crash debris). */
  sparkBurst(
    x: number,
    y: number,
    z: number,
    color: string,
    count: number,
    speed = 7,
    duration = 0.45,
  ): void {
    let spawned = 0;
    for (const spark of this.sparks) {
      if (spawned >= count) break;
      if (spark.life > 0) continue;
      const angle = (spawned / count) * Math.PI * 2 + this.jitter() * 0.8;
      spark.material.color.set(color);
      spark.mesh.position.set(x, y, z);
      spark.velocity.set(Math.cos(angle) * speed, 2.5 + this.jitter() * 3, Math.sin(angle) * speed * 0.4);
      spark.life = duration;
      spark.duration = duration;
      spark.mesh.visible = true;
      spawned += 1;
    }
  }

  update(delta: number): void {
    for (const ring of this.rings) {
      if (ring.life <= 0) continue;
      ring.life -= delta;
      const t = 1 - Math.max(ring.life, 0) / ring.duration;
      const eased = 1 - (1 - t) * (1 - t);
      ring.mesh.scale.setScalar(ring.fromScale + (ring.toScale - ring.fromScale) * eased);
      ring.material.opacity = (1 - t) * 0.85;
      if (ring.life <= 0) ring.mesh.visible = false;
    }
    for (const spark of this.sparks) {
      if (spark.life <= 0) continue;
      spark.life -= delta;
      spark.velocity.y -= 9 * delta;
      spark.mesh.position.addScaledVector(spark.velocity, delta);
      spark.mesh.lookAt(spark.mesh.position.x + spark.velocity.x, spark.mesh.position.y + spark.velocity.y, spark.mesh.position.z + spark.velocity.z);
      spark.material.opacity = (spark.life / spark.duration) * 0.9;
      if (spark.life <= 0) spark.mesh.visible = false;
    }
  }

  reset(): void {
    for (const ring of this.rings) {
      ring.life = 0;
      ring.mesh.visible = false;
    }
    for (const spark of this.sparks) {
      spark.life = 0;
      spark.mesh.visible = false;
    }
  }

  dispose(): void {
    for (const item of this.disposables) item.dispose();
  }
}
